import { Button, Card, CardBody, Input, Textarea } from '@heroui/react';
import { useMutation, useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import SEOHead from '../components/SEOHead';
import { contentService } from '../services/contentService';
import { createSafeHtmlProps } from '../utils/htmlUtils';

const Contact: React.FC = () => {
  const { data } = useQuery({
    queryKey: ['content', 'contact'],
    queryFn: () => contentService.getContentPage('contact'),
  });
  const pageTitle = data?.title || 'Contact Us';
  const pageDescription =
    data?.summary ||
    'Contact CodedGhost Minis for order support, questions, or custom requests.';

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  const sendMutation = useMutation({
    mutationFn: () =>
      contentService.sendContactMessage({
        name,
        email,
        subject: subject || undefined,
        message,
      }),
    onSuccess: () => {
      toast.success('Message sent. We will get back to you soon.');
      setName('');
      setEmail('');
      setSubject('');
      setMessage('');
    },
    onError: () => {
      toast.error('Failed to send message. Please try again later.');
    },
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!name || !email || !message) {
      toast.error('Please fill in your name, email, and message.');
      return;
    }
    sendMutation.mutate();
  };

  return (
    <>
      <SEOHead title={pageTitle} description={pageDescription} />
      <div className='max-w-5xl mx-auto p-6'>
        <h1 className='text-3xl font-bold mb-4'>
          {data?.title || 'Contact Us'}
        </h1>
        {data?.summary && <p className='text-gray-600 mb-6'>{data.summary}</p>}

        <div className='grid grid-cols-1 md:grid-cols-2 gap-6'>
          <Card>
            <CardBody>
              <div
                {...createSafeHtmlProps(
                  data?.bodyHtml || '',
                  'prose max-w-none'
                )}
              />
            </CardBody>
          </Card>

          <Card>
            <CardBody>
              <form className='space-y-4' onSubmit={onSubmit}>
                <Input
                  label='Your Name'
                  placeholder='Enter your name'
                  value={name}
                  onChange={e => setName(e.target.value)}
                  isRequired
                />
                <Input
                  label='Your Email'
                  placeholder='Enter your email'
                  type='email'
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  isRequired
                />
                <Input
                  label='Subject (optional)'
                  placeholder='What is this about?'
                  value={subject}
                  onChange={e => setSubject(e.target.value)}
                />
                <Textarea
                  label='Message'
                  placeholder='How can we help?'
                  minRows={4}
                  value={message}
                  onChange={e => setMessage(e.target.value)}
                  isRequired
                />
                <Button
                  color='primary'
                  type='submit'
                  isLoading={sendMutation.isPending}
                >
                  Send Message
                </Button>
              </form>
            </CardBody>
          </Card>
        </div>
      </div>
    </>
  );
};

export default Contact;
